import type { FileParent } from "@msuite/citadel";
import { Button, ButtonGroup } from "@msuite/ui/button";
import { FileUpload, useFileUpload } from "@msuite/ui/file-upload";
import type { FileInspectorProps, ItemFiles } from "@msuite/ui/files";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { FileCollectionTable } from "@msuite/utils/types";
import { linkFiles, unlinkFile, useUpload } from "@/api/files";
import { AnnotatedFileInspector } from "@/components/lightbox";
import { useFileCapabilities } from "@/hooks/use-file-capabilities";
import { useRemoteFileUrl } from "@/hooks/use-remote-file-url";
import { PickProjectGalleryFilesModal } from "@/modals/projects/pick-project-gallery-files-modal";
import { FileCardRemote } from "./file-card-remote";

/** Props Interface */
interface RemoteFileInspectorProps<T extends { id: string }> {
	itemFiles: ItemFiles<T>;
	tableName: FileCollectionTable;
	foreignKey: string;
	identifier: string;
	/**
	 * The record `identifier` names, in the shape its rule binds on — a ticket as
	 * `{ ticket: { id, projectId } }`, any other parent as `{ subject, resource }`. Every write the
	 * inspector offers follows this parent's `update`; `undefined` while it is still loading.
	 */
	parent: FileParent | undefined;
	size?: "xs" | "sm";
	variant?: "modal" | "inline";
	onInteraction?: (fileId: string) => void | Promise<void>;
	onUnlinked?: (fileId: string) => void | Promise<void>;
	withUpload?: boolean;
	/** Extra foreign key values required to link a new file (e.g. `ticketFiles` also needs `projectId`). */
	additionalForeignKeyValues?: Record<string, string>;
	/** Enables an "select existing files" trigger that picks from this project's file gallery. */
	projectId?: string;
	onLinked?: () => void | Promise<void>;
	showEmpty?: boolean;
}

/**
 * Drop-in file list for any `xxxxFiles` junction table (e.g. `noteFiles`, `ticketFiles`).
 * Wires up `AnnotatedFileInspector` and `FileCardRemote` and calls `unlinkFile` for the
 * given table/foreign key/identifier, so callers only need to say what should happen
 * afterwards instead of re-implementing the same render/unlink plumbing every time.
 * Optionally also renders, inside the "view all files" modal (never inline on the page),
 * an upload trigger that stages/uploads/links new files, and/or (when `projectId` is given)
 * a "select existing files" trigger that links files already uploaded elsewhere in the project.
 *
 * @param itemFiles - The junction rows to render, each carrying its linked `file`.
 * @param tableName - The junction table to unlink/link (e.g. `"ticketFiles"`).
 * @param foreignKey - The foreign key column identifying the parent record (e.g. `"ticketId"`).
 * @param identifier - The parent record's id.
 * @param parent - The parent record, which every attach, unlink and annotate control is asked of.
 * @param size - Forwarded to `AnnotatedFileInspector` for text/thumbnail sizing.
 * @param variant - Forwarded to `AnnotatedFileInspector` (`"modal"` previews or `"inline"` thumbnails).
 * @param onInteraction - Called whenever a file card is interacted with (e.g. deleted).
 * @param onUnlinked - Called after a file has been successfully unlinked.
 * @param withUpload - Renders a paperclip trigger inside the file list modal to stage, upload, and link new files.
 * @param additionalForeignKeyValues - Extra foreign key values required to link a new file, beyond `foreignKey`/`identifier`.
 * @param projectId - When given, renders a trigger to pick already-uploaded files from this project's gallery.
 * @param onLinked - Called after files have been uploaded/selected and linked.
 */
export function RemoteFileInspector<T extends { id: string }>({
	itemFiles,
	tableName,
	foreignKey,
	identifier,
	parent,
	size,
	variant,
	onInteraction,
	onUnlinked,
	withUpload = false,
	additionalForeignKeyValues,
	projectId,
	onLinked,
	showEmpty = true,
}: RemoteFileInspectorProps<T>) {
	/** Hooks */
	const { t } = useTranslation();
	const files = useFileCapabilities(parent);
	const uploadDisclosure = useDisclosure();
	const pickExistingDisclosure = useDisclosure();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({ uploadFn });

	/** Functions */
	async function handleOnUnlink(fileId: string) {
		await unlinkFile(tableName, foreignKey, identifier, fileId);
		await onUnlinked?.(fileId);
	}

	async function handleOnUploadFiles() {
		const uploadResults = await controller.handleOnFileUpload();
		if (!uploadResults.length) return;
		await linkFiles(
			tableName,
			{ [foreignKey]: identifier, ...additionalForeignKeyValues },
			{ uploadResults },
		);
		controller.handleOnReset();
		uploadDisclosure.onClose();
		await onLinked?.();
	}

	async function handleOnSelectExistingFiles(existingFileIds: string[]) {
		if (!existingFileIds.length) return;
		await linkFiles(
			tableName,
			{ [foreignKey]: identifier, ...additionalForeignKeyValues },
			{ existingFileIds },
		);
		await onLinked?.();
	}

	/** Computed */
	// Everything the inspector writes hangs on one parent, so one answer decides the paperclip, the
	// gallery picker, the cards' unlink and delete, and whether the annotator draws.
	const withAttach = withUpload && files.canWrite;
	const withPick = !!projectId && files.canWrite;

	/** Guard */
	if (!showEmpty && !itemFiles.length) return null;

	/** Render */
	return (
		<>
			<AnnotatedFileInspector
				{...({
					itemFiles,
					size,
					variant,
					useRemoteFileUrl,
					canAnnotate: files.canWrite,
					renderFile: (file) => (
						<FileCardRemote
							fileId={file.id}
							canRemove={files.canWrite}
							onInteraction={async () => {
								await onInteraction?.(file.id);
							}}
							onUnlink={() => handleOnUnlink(file.id)}
						/>
					),
					bodyContent: withAttach && uploadDisclosure.isOpen && (
						<div className="!bg-primaryGray-50 dark:!bg-primaryGray-800 p-0.5 border-[0.5px] rounded-2xl card">
							<FileUpload
								withLabel={false}
								controller={controller}
								renderFile={() => null}
							/>
						</div>
					),
					footerContent: (withAttach || withPick) && (
						<ButtonGroup isAttached={false}>
							{withAttach && (
								<Button
									variant="white"
									icon={<Icon name="Paperclip" />}
									onClick={uploadDisclosure.onToggle}
								/>
							)}
							{withPick && (
								<Button
									variant="white"
									onClick={pickExistingDisclosure.onOpen}
								>
									{t("general.select_existing_files")}
								</Button>
							)}
							{withAttach && uploadDisclosure.isOpen && controller.hasFiles && (
								<Button
									variant="primary"
									onClick={handleOnUploadFiles}
									isLoading={controller.isUploading}
								>
									{t("file_upload.upload_files")}
								</Button>
							)}
						</ButtonGroup>
					),
				} as FileInspectorProps<T>)}
			/>
			{pickExistingDisclosure.isOpen && projectId && (
				<PickProjectGalleryFilesModal
					projectId={projectId}
					excludeFileIds={itemFiles.map((item) => item.file.id)}
					isOpen={pickExistingDisclosure.isOpen}
					onClose={pickExistingDisclosure.onClose}
					onComplete={handleOnSelectExistingFiles}
				/>
			)}
		</>
	);
}
